// Toolbar "Payment" intercept (GDN-2405).
//
// Front desks collect copays through this button today, which mints unlinked
// ad-hoc charges that double-count when the ERA posts the same responsibility
// later. One beat between click and money: an anchored popover offers two
// explicit, visually equal routes — "Collect co-pay for a visit" (opens the
// patient ledger drawer with the settle sheet preset to the copay
// declaration, threading the toolbar's encounter context) and the existing
// the "Collect payment" flow (gdn-3154: one name at every entry).
//
// Without a patient in context the copay route has nothing to link, so the
// click falls through to today's direct payment-request flow unchanged.

import React, { type ReactNode, useCallback, useState } from 'react';
import {
  useOpenLedgerDrawer,
  useOpenPaymentSheet,
} from '@features/patient-ledger';
import { useRequestPayment } from '@features/payments/hooks/useRequestPayment';
import { usePatientPaymentMethods } from '@features/payments/store/hooks';
import { Payment as PaymentIcon } from '@mui/icons-material';
import CreditCardOutlinedIcon from '@mui/icons-material/CreditCardOutlined';
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined';
import { Box, Popover, Typography } from '@mui/material';
import {
  FeatureGates,
  useEventTracking,
  useFeatureGate,
} from '@services/statsig';

import { ToolbarButton } from '../components/ToolbarButton';
import { useToolbarAction } from '../store/hooks';
import { type ToolbarActionProps } from '../types';

export const RequestPaymentToolbarAction: React.FC<ToolbarActionProps> = ({
  context,
  compact,
  presentation,
}) => {
  const { badgeCount } = useToolbarAction('request-payment');
  const { toolbar } = useEventTracking();
  const openLedgerDrawer = useOpenLedgerDrawer();
  const openPaymentSheet = useOpenPaymentSheet();
  // gdn-2957: the copay route is a money action with an explicit method,
  // so gated it opens the page sheet instead of the drawer.
  const useSheet = useFeatureGate(FeatureGates.PATIENT_BALANCE_TABLE);

  // Anchor measured post-layout via a callback ref — opening against a rect
  // captured in the same commit renders the popover clipped at the viewport
  // corner (mount-race fix ported from the GDN-2400 prototype).
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [open, setOpen] = useState(false);

  // Check if patient has payment methods on file
  const { paymentMethods } = usePatientPaymentMethods(context?.patientId ?? '');
  const hasPaymentMethod = paymentMethods.length > 0;
  // Find the default payment method, or fall back to the first one
  const defaultPaymentMethod = paymentMethods.find(pm => pm.is_default) ?? paymentMethods[0];

  // Use the headless payment request hook
  const { triggerPaymentRequest } = useRequestPayment({
    patientId: context?.patientId,
    encounterId: context?.encounterId,
    hasPaymentMethod,
    defaultPaymentMethod,
    paymentMethods,
    source: 'toolbar',
  });

  const handleClick = useCallback(() => {
    // Track toolbar action click
    toolbar.actionClicked({
      action_type: hasPaymentMethod ? 'charge_payment' : 'request_payment',
      has_patient: !!context?.patientId,
    });
    if (!context?.patientId) {
      // No patient in context — nothing to declare a co-pay against. Keep
      // today's direct flow, which owns patient resolution.
      triggerPaymentRequest();
      return;
    }
    setOpen(true);
  }, [toolbar, context?.patientId, hasPaymentMethod, triggerPaymentRequest]);

  const handleCopayRoute = useCallback(() => {
    setOpen(false);
    toolbar.copayRouteSelected({
      encounter_id: context?.encounterId,
      has_encounter: !!context?.encounterId,
      source: 'toolbar',
    });
    const action = {
      kind: 'settle' as const,
      method: 'cash' as const,
      paymentIntent: 'copay' as const,
    };
    if (useSheet) {
      openPaymentSheet({
        patientId: context?.patientId ?? '',
        encounterId: context?.encounterId ?? null,
        action,
        source: 'toolbar_copay_route',
      });
      return;
    }
    openLedgerDrawer({
      patientId: context?.patientId ?? '',
      encounterId: context?.encounterId ?? null,
      initialTab: 'ledger',
      initialAction: action,
      source: 'toolbar_copay_route',
    });
  }, [
    context?.encounterId,
    context?.patientId,
    openLedgerDrawer,
    openPaymentSheet,
    toolbar,
    useSheet,
  ]);

  const handleChargeRoute = useCallback(() => {
    setOpen(false);
    toolbar.chargeRouteSelected({
      has_card_on_file: hasPaymentMethod,
      source: 'toolbar',
    });
    triggerPaymentRequest();
  }, [hasPaymentMethod, toolbar, triggerPaymentRequest]);

  // One name for the flow (gdn-3154); the tooltip only adds what the
  // label cannot say. It no longer varies on whether a card is on file:
  // the sheet offers card, invoice and cash together now, so promising
  // one instrument up front is the four-names problem in miniature.
  const tooltipTitle = context?.patientId
    ? 'Collect payment'
    : 'Collect payment (select patient in modal)';

  return (
    <>
      <ToolbarButton
        ref={setAnchorEl}
        icon={PaymentIcon}
        label="Collect payment"
        tooltip={tooltipTitle}
        onClick={handleClick}
        compact={compact}
        badgeCount={badgeCount}
        dataAttribute="toolbar-request-payment"
        presentation={presentation}
        menuLabel="Collect payment"
      />
      <Popover
        open={open && !!anchorEl}
        anchorEl={anchorEl}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        slotProps={{
          paper: {
            sx: {
              width: 336,
              borderRadius: 2,
              overflow: 'hidden',
            },
          },
        }}
      >
        <Box sx={{ p: 1, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
          <RouteOption
            icon={<EventAvailableOutlinedIcon />}
            title="Collect co-pay for a visit"
            sub="Records it against the visit so insurance reconciles it automatically"
            onClick={handleCopayRoute}
          />
          <RouteOption
            icon={<CreditCardOutlinedIcon />}
            title="Payment not tied to a visit"
            sub="Ad-hoc charge or payment link — not tied to a visit"
            onClick={handleChargeRoute}
          />
        </Box>
      </Popover>
    </>
  );
};

// Two visually equal routes — no badge, no emphasis box.
const RouteOption = ({
  icon,
  title,
  sub,
  onClick,
}: {
  icon: ReactNode;
  title: string;
  sub: string;
  onClick: () => void;
}) => (
  <Box
    role="button"
    aria-label={title}
    onClick={onClick}
    sx={{
      display: 'flex',
      gap: 1.25,
      alignItems: 'flex-start',
      px: 1.5,
      py: 1.25,
      borderRadius: 1.5,
      cursor: 'pointer',
      '&:hover': { bgcolor: 'action.hover' },
    }}
  >
    <Box
      sx={{
        color: 'text.secondary',
        mt: '2px',
        display: 'flex',
        '& svg': { fontSize: 20 },
      }}
    >
      {icon}
    </Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
        {title}
      </Typography>
      <Typography
        variant="caption"
        sx={{ color: 'text.secondary', display: 'block', lineHeight: 1.35 }}
      >
        {sub}
      </Typography>
    </Box>
  </Box>
);
